// @ts-nocheck
import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { toast } from 'sonner';
import { useWorkspace } from '@/contexts/context';
import { hasPermission, type UserRole } from '@product-radar/shared';
import { supabase } from '@product-radar/shared';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Separator } from './ui/separator';
import {
  Plus,
  Settings,
  CheckCircle,
  XCircle,
  Loader2,
  MessageSquare,
  Database,
  FileText,
  ArrowLeft,
  ArrowRight,
  ExternalLink,
  Unplug,
  Hash,
  Plug,
  AlertTriangle,
  Activity,
  Send,
} from 'lucide-react';
import { Textarea } from './ui/textarea';
import { storeSlackOAuthState, getWorkspaceIdFromSlackState } from '@/lib/slack-oauth';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CredentialField {
  key: string;
  label: string;
  placeholder: string;
  type?: 'text' | 'password' | 'url';
  helpText?: string;
}

interface IntegrationDefinition {
  id: string;
  name: string;
  description: string;
  icon: ReactNode;
  category: string;
  connectVia?: 'oauth' | 'credentials';
  steps: {
    setup: { title: string; instructions: ReactNode };
    credentials: { fields: CredentialField[] };
  };
  configFields?: CredentialField[];
}

type ConnectionStatus = 'connected' | 'available';

// ---------------------------------------------------------------------------
// Integration definitions
// ---------------------------------------------------------------------------

const INTEGRATIONS: IntegrationDefinition[] = [
  {
    id: 'slack',
    name: 'Slack',
    description: 'Connect Slack channels to automatically collect feedback from team discussions and receive notifications.',
    icon: <MessageSquare className="h-6 w-6" />,
    category: 'Communication',
    connectVia: 'oauth',
    steps: {
      setup: { title: '', instructions: null },
      credentials: { fields: [] },
    },
  },
  {
    id: 'jira',
    name: 'Jira',
    description: 'Export prioritized ideas directly to Jira as issues or epics.',
    icon: <FileText className="h-6 w-6" />,
    category: 'Project Management',
    steps: {
      setup: {
        title: 'Create a Jira API Token',
        instructions: (
          <div className="space-y-3 text-sm text-muted-foreground">
            <ol className="list-decimal list-inside space-y-2">
              <li>
                Go to{' '}
                <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noopener noreferrer" className="text-primary underline">
                  Atlassian API Tokens
                </a>{' '}
                and click <strong>Create API token</strong>.
              </li>
              <li>Give the token a descriptive label (e.g. "Product Radar").</li>
              <li>Copy the generated token -- you won't be able to see it again.</li>
              <li>Note your Jira instance URL (e.g. <code className="bg-muted px-1 rounded text-xs">https://yourcompany.atlassian.net</code>).</li>
            </ol>
          </div>
        ),
      },
      credentials: {
        fields: [
          { key: 'instance_url', label: 'Jira Instance URL', placeholder: 'https://yourcompany.atlassian.net', type: 'url', helpText: 'Your Jira Cloud site URL.' },
          { key: 'email', label: 'Email', placeholder: 'you@company.com', type: 'text', helpText: 'The email associated with the API token.' },
          { key: 'api_token', label: 'API Token', placeholder: 'Paste your API token', type: 'password', helpText: 'Generated from Atlassian account settings.' },
        ],
      },
    },
    configFields: [
      { key: 'instance_url', label: 'Jira Instance URL', placeholder: 'https://yourcompany.atlassian.net', type: 'url' },
      { key: 'email', label: 'Email', placeholder: 'you@company.com', type: 'text' },
      { key: 'api_token', label: 'API Token', placeholder: 'API token', type: 'password' },
    ],
  },
  {
    id: 'salesforce',
    name: 'Salesforce',
    description: 'Sync customer feedback from Salesforce notes, cases, and interactions.',
    icon: <Database className="h-6 w-6" />,
    category: 'CRM',
    steps: {
      setup: {
        title: 'Create a Salesforce Connected App',
        instructions: (
          <div className="space-y-3 text-sm text-muted-foreground">
            <ol className="list-decimal list-inside space-y-2">
              <li>In Salesforce, navigate to <strong>Setup</strong> &rarr; <strong>App Manager</strong> &rarr; <strong>New Connected App</strong>.</li>
              <li>Fill in the basic info (name, contact email).</li>
              <li>
                Enable <strong>OAuth Settings</strong>. Add the callback URL and select scopes:{' '}
                <code className="bg-muted px-1 rounded text-xs">Full access (full)</code> or specific scopes like{' '}
                <code className="bg-muted px-1 rounded text-xs">api</code>.
              </li>
              <li>Save and wait a few minutes for the app to be ready.</li>
              <li>Note the <strong>Consumer Key</strong> (Client ID) and <strong>Consumer Secret</strong> (Client Secret).</li>
            </ol>
          </div>
        ),
      },
      credentials: {
        fields: [
          { key: 'instance_url', label: 'Instance URL', placeholder: 'https://yourcompany.salesforce.com', type: 'url', helpText: 'Your Salesforce org URL.' },
          { key: 'client_id', label: 'Consumer Key (Client ID)', placeholder: 'Paste your Consumer Key', type: 'text', helpText: 'Found in the Connected App settings.' },
          { key: 'client_secret', label: 'Consumer Secret (Client Secret)', placeholder: 'Paste your Consumer Secret', type: 'password', helpText: 'Found in the Connected App settings.' },
        ],
      },
    },
    configFields: [
      { key: 'instance_url', label: 'Instance URL', placeholder: 'https://yourcompany.salesforce.com', type: 'url' },
      { key: 'client_id', label: 'Consumer Key', placeholder: 'Consumer Key', type: 'text' },
      { key: 'client_secret', label: 'Consumer Secret', placeholder: 'Consumer Secret', type: 'password' },
    ],
  },
];

// ---------------------------------------------------------------------------
// Step indicator
// ---------------------------------------------------------------------------

const STEP_LABELS = ['Setup', 'Enter Details', 'Test Connection'];

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center justify-center gap-0 mb-6">
      {STEP_LABELS.map((label, idx) => {
        const stepNum = idx + 1;
        const isActive = stepNum === current;
        const isDone = stepNum < current;
        return (
          <div key={label} className="flex items-center">
            {idx > 0 && (
              <div className={`w-12 h-0.5 ${isDone ? 'bg-primary' : 'bg-border'}`} />
            )}
            <div className="flex flex-col items-center gap-1">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium border-2 transition-colors ${
                  isDone
                    ? 'bg-primary border-primary text-primary-foreground'
                    : isActive
                      ? 'border-primary text-primary bg-background'
                      : 'border-border text-muted-foreground bg-background'
                }`}
              >
                {isDone ? <CheckCircle className="h-4 w-4" /> : stepNum}
              </div>
              <span className={`text-[11px] whitespace-nowrap ${isActive || isDone ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                {label}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Slack OAuth constants
// ---------------------------------------------------------------------------

const SLACK_SCOPES = 'chat:write,channels:join,channels:history,channels:read,groups:history,groups:read,users:read';

const SLACK_STEP_LABELS = ['Add to Slack', 'Configure', 'Test'];

function SlackStepIndicator({ current, connected }: { current: number; connected: boolean }) {
  return (
    <div className="flex items-center justify-center gap-0 mb-6">
      {SLACK_STEP_LABELS.map((label, idx) => {
        const stepNum = idx + 1;
        const isActive = stepNum === current;
        const isDone = (stepNum === 1 && connected) || stepNum < current;
        return (
          <div key={label} className="flex items-center">
            {idx > 0 && (
              <div className={`w-12 h-0.5 ${isDone ? 'bg-primary' : 'bg-border'}`} />
            )}
            <div className="flex flex-col items-center gap-1">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium border-2 transition-colors ${
                  isDone
                    ? 'bg-primary border-primary text-primary-foreground'
                    : isActive
                      ? 'border-primary text-primary bg-background'
                      : 'border-border text-muted-foreground bg-background'
                }`}
              >
                {isDone ? <CheckCircle className="h-4 w-4" /> : stepNum}
              </div>
              <span className={`text-[11px] whitespace-nowrap ${isActive || isDone ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                {label}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Slack Connect Wizard (3-step: Add to Slack → Configure → Test)
// ---------------------------------------------------------------------------

function SlackConnectWizard({
  open,
  onOpenChange,
  isConnected,
  onDisconnect,
  onConnected,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isConnected: boolean;
  onDisconnect: () => void;
  onConnected: () => void;
}) {
  const { workspace, membership } = useWorkspace();
  const clientId = import.meta.env.VITE_PUBLIC_SLACK_CLIENT_ID;

  const [step, setStep] = useState(isConnected ? 2 : 1);
  const [slackChannelId, setSlackChannelId] = useState<string | null>(null);
  const [slackChannels, setSlackChannels] = useState<{ id: string; name: string; isPrivate: boolean }[]>([]);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [channelSaving, setChannelSaving] = useState(false);
  const [frequency, setFrequency] = useState<'immediate' | 'daily' | 'weekly'>('immediate');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<'idle' | 'success' | 'error'>('idle');
  const [manualInviteNeeded, setManualInviteNeeded] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  useEffect(() => {
    if (isConnected && step === 1) setStep(2);
  }, [isConnected, step]);

  const loadChannels = useCallback(async () => {
    if (!workspace) return;
    setChannelsLoading(true);
    try {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const fnUrl = `https://${projectId}.supabase.co/functions/v1/slack-channels`;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      const res = await fetch(fnUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ workspace_id: workspace.id }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Failed to load channels');
      setSlackChannels(result.channels || []);
    } catch {
      toast.error('Failed to load Slack channels');
    } finally {
      setChannelsLoading(false);
    }
  }, [workspace?.id]);

  const fetchCurrentChannel = useCallback(async () => {
    if (!workspace) return;
    const { data } = await supabase.rpc('get_slack_connection_status', { p_workspace_id: workspace.id });
    const row = Array.isArray(data) ? data[0] : data;
    setSlackChannelId(row?.slack_channel_id || null);
  }, [workspace?.id]);

  const loadPrefs = useCallback(() => {
    const raw = membership?.notification_preferences;
    if (raw?.feedback?.frequency) setFrequency((raw.feedback.frequency as 'immediate' | 'daily' | 'weekly') || 'immediate');
  }, [membership?.notification_preferences]);

  useEffect(() => {
    if (open) {
      if (isConnected) {
        fetchCurrentChannel();
        loadChannels();
        loadPrefs();
      }
    }
  }, [open, isConnected, fetchCurrentChannel, loadChannels, loadPrefs]);

  const joinBotToChannel = async (channelId: string): Promise<{ joined: boolean; needsManualInvite: boolean }> => {
    try {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const fnUrl = `https://${projectId}.supabase.co/functions/v1/slack-channels`;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return { joined: false, needsManualInvite: false };
      const res = await fetch(fnUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ workspace_id: workspace!.id, action: 'join_channel', channel_id: channelId }),
      });
      const result = await res.json();
      return { joined: !!result.joined, needsManualInvite: !!result.needs_manual_invite };
    } catch {
      return { joined: false, needsManualInvite: false };
    }
  };

  const handleAddToSlack = () => {
    if (!clientId || !workspace) return;
    const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;
    const redirectUri = `${appUrl.replace(/\/$/, '')}/integrations`;
    const state = storeSlackOAuthState(workspace.id);
    window.location.href = `https://slack.com/oauth/v2/authorize?client_id=${clientId}&scope=${encodeURIComponent(SLACK_SCOPES)}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${encodeURIComponent(state)}`;
  };

  const handleChannelChange = async (channelId: string) => {
    if (!workspace) return;
    setChannelSaving(true);
    setManualInviteNeeded(false);
    try {
      const { error } = await supabase.rpc('update_slack_notification_channel', {
        p_workspace_id: workspace.id,
        p_slack_channel_id: channelId === '_none' ? null : channelId,
      });
      if (error) throw error;
      setSlackChannelId(channelId === '_none' ? null : channelId);
      if (channelId !== '_none') {
        const { joined, needsManualInvite } = await joinBotToChannel(channelId);
        if (joined) toast.success('Channel saved. Bot added.');
        else if (needsManualInvite) {
          setManualInviteNeeded(true);
          const ch = slackChannels.find((c) => c.id === channelId);
          toast.info('Channel saved. Invite the bot: /invite @ProductRadar in #' + (ch?.name || 'channel'));
        } else toast.success('Channel saved');
      } else toast.success('Channel removed');
    } catch (e: any) {
      toast.error('Failed to update channel', { description: e?.message });
    } finally {
      setChannelSaving(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!workspace || !membership) return;
    try {
      const raw = membership?.notification_preferences ?? {};
      const prefs = {
        feedback: {
          email: raw.feedback?.email ?? true,
          slack: true,
          frequency,
        },
        ideas: {
          email: raw.ideas?.email ?? true,
          slack: true,
          frequency,
        },
      };
      const { error } = await supabase.rpc('update_member_notification_preferences', {
        p_workspace_id: workspace.id,
        p_prefs: prefs,
      });
      if (error) throw error;
      toast.success('Settings saved');
      setStep(3);
    } catch (e: any) {
      toast.error('Failed to save', { description: e?.message });
    }
  };

  const handleTest = async () => {
    if (!workspace) return;
    setTesting(true);
    setTestResult('idle');
    try {
      const { data } = await supabase.rpc('get_slack_connection_status', { p_workspace_id: workspace.id });
      const row = Array.isArray(data) ? data[0] : data;
      if (!row?.slack_team_id) throw new Error('No connection');
      if (slackChannelId) {
        const { joined, needsManualInvite } = await joinBotToChannel(slackChannelId);
        if (!joined && needsManualInvite) {
          setManualInviteNeeded(true);
          setTestResult('error');
          return;
        }
      }
      setManualInviteNeeded(false);
      setTestResult('success');
    } catch {
      setTestResult('error');
    } finally {
      setTesting(false);
    }
  };

  const handleDisconnect = () => {
    onDisconnect();
    setConfirmDisconnect(false);
    onOpenChange(false);
    toast.success('Slack disconnected.');
  };

  const handleDone = () => {
    onConnected();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 bg-muted rounded-lg">
              <MessageSquare className="h-6 w-6" />
            </div>
            <div>
              <DialogTitle>Connect Slack</DialogTitle>
              <DialogDescription>Connect your Slack workspace to Product Radar.</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <SlackStepIndicator current={step} connected={isConnected} />

        {/* Step 1: Add to Slack */}
        {step === 1 && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Connect your Slack workspace to Product Radar to enable:
            </p>
            <ul className="text-sm text-muted-foreground space-y-2">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span><strong>Send to Product Radar</strong> message shortcut for forwarding conversations as feedback</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>Notifications for new feedback and ideas in your chosen channel</span>
              </li>
            </ul>
            <div className="rounded-lg border bg-muted/30 overflow-hidden">
              <img src="/slack-add-to-radar.png" alt="Add to Product Radar shortcut in Slack" className="w-full object-contain max-h-40" />
            </div>
            <p className="text-sm text-muted-foreground">
              Clicking the button below will take you to Slack to authorize Product Radar. Once approved, the app will be installed to your workspace.
            </p>
            <Button onClick={handleAddToSlack} disabled={!clientId} className="w-full">
              {!clientId ? 'Slack Client ID not configured' : 'Add to Slack'}
              {clientId && <ExternalLink className="h-4 w-4 ml-2" />}
            </Button>
          </div>
        )}

        {/* Step 2: Configure */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Hash className="h-4 w-4" /> Notification Channel
              </Label>
              <Select value={slackChannelId || '_none'} onValueChange={handleChannelChange} disabled={channelSaving}>
                <SelectTrigger>
                  <SelectValue placeholder={channelsLoading ? 'Loading...' : 'Select a channel'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">No channel selected</SelectItem>
                  {slackChannels.map((ch) => (
                    <SelectItem key={ch.id} value={ch.id}>
                      #{ch.name} {ch.isPrivate ? '(private)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notification frequency</Label>
              <Select value={frequency} onValueChange={(v) => setFrequency(v as any)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="immediate">Immediate</SelectItem>
                  <SelectItem value="daily">Daily digest</SelectItem>
                  <SelectItem value="weekly">Weekly digest</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {manualInviteNeeded && slackChannelId && (
              <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30 p-3">
                <MessageSquare className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  Private channel: type <code className="bg-amber-100 dark:bg-amber-900/50 px-1 rounded text-xs">/invite @ProductRadar</code> in the channel.
                </p>
              </div>
            )}
            <Button onClick={handleSaveConfig} disabled={channelSaving} className="w-full">
              {channelSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </div>
        )}

        {/* Step 3: Test */}
        {step === 3 && (
          <div className="flex flex-col items-center justify-center py-6 space-y-4">
            <CheckCircle className="h-12 w-12 text-green-500" />
            <p className="font-medium">Integration complete</p>
            <p className="text-sm text-muted-foreground text-center">Test the connection to verify everything works.</p>
            <Button variant="outline" onClick={handleTest} disabled={testing}>
              {testing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Test connection
            </Button>
            {testResult === 'success' && <p className="text-sm text-green-600 flex items-center gap-1"><CheckCircle className="h-4 w-4" /> Connection verified.</p>}
            {testResult === 'error' && <p className="text-sm text-destructive flex items-center gap-1"><XCircle className="h-4 w-4" /> Connection check failed.</p>}
          </div>
        )}

        <DialogFooter className="flex justify-between sm:justify-between">
          <div>
            {step > 1 && step < 3 && (
              <Button variant="ghost" size="sm" onClick={() => setStep(step - 1)}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
            )}
            {step === 3 && (
              <Button variant="ghost" size="sm" onClick={() => setStep(2)}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {step === 1 && (
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            )}
            {step === 2 && (
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            )}
            {step === 3 && (
              <Button onClick={handleDone}>Done</Button>
            )}
          </div>
        </DialogFooter>

        {isConnected && !confirmDisconnect && (
          <div className="pt-2 border-t">
            <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => setConfirmDisconnect(true)}>
              <Unplug className="h-4 w-4 mr-1" /> Disconnect Slack
            </Button>
          </div>
        )}
        {confirmDisconnect && (
          <div className="flex items-center justify-between bg-destructive/10 p-3 rounded-lg mt-2">
            <p className="text-sm text-destructive">Remove the integration?</p>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setConfirmDisconnect(false)}>Cancel</Button>
              <Button variant="destructive" size="sm" onClick={handleDisconnect}>Disconnect</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Credentials Connect Wizard Dialog (for Jira, Salesforce)
// ---------------------------------------------------------------------------

function ConnectWizard({
  integration,
  workspace,
  open,
  onOpenChange,
  onConnected,
}: {
  integration: IntegrationDefinition;
  workspace: { id: string } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConnected: () => void;
}) {
  const [step, setStep] = useState(1);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<'idle' | 'success' | 'error'>('idle');
  const [testError, setTestError] = useState('');

  const resetState = () => {
    setStep(1);
    setFieldValues({});
    setTesting(false);
    setTestResult('idle');
    setTestError('');
  };

  const handleOpenChange = (v: boolean) => {
    if (!v) resetState();
    onOpenChange(v);
  };

  const fields = integration.steps.credentials.fields;
  const allFieldsFilled = fields.every((f) => (fieldValues[f.key] || '').trim().length > 0);

  const runTest = async () => {
    setTesting(true);
    setTestResult('idle');
    setTestError('');

    try {
      if (integration.id === 'jira') {
        const url = fieldValues['instance_url']?.replace(/\/$/, '');
        const email = fieldValues['email'];
        const token = fieldValues['api_token'];
        if (!url || !email || !token) throw new Error('Missing fields');

        const { data, error } = await supabase.functions.invoke('jira-test', {
          body: { instance_url: url, email, api_token: token },
        });
        if (error) throw new Error(error.message);
        if (data?.error || data?.ok === false) throw new Error(data?.error || data?.details || 'Connection failed');
        setTestResult('success');
      } else if (integration.id === 'salesforce') {
        const url = fieldValues['instance_url']?.replace(/\/$/, '');
        if (!url) throw new Error('Missing instance URL');
        setTestResult('success');
        toast.info('Salesforce integration is not yet fully implemented. Connection saved locally.');
      }
    } catch (e: any) {
      setTestResult('error');
      setTestError(e?.message || 'Connection failed');
    } finally {
      setTesting(false);
    }
  };

  const handleDone = async () => {
    try {
      if (integration.id === 'jira' && workspace) {
        const url = fieldValues['instance_url']?.replace(/\/$/, '');
        const email = fieldValues['email'];
        const token = fieldValues['api_token'];
        if (!url || !email || !token) throw new Error('Missing fields');
        const { error } = await supabase.rpc('save_jira_connection', {
          p_workspace_id: workspace.id,
          p_instance_url: url,
          p_email: email,
          p_api_token: token,
        });
        if (error) throw error;
      }
      onConnected();
      handleOpenChange(false);
      toast.success(`${integration.name} connected!`);
    } catch (e: any) {
      toast.error('Failed to save connection', { description: e?.message });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 bg-muted rounded-lg">{integration.icon}</div>
            <div>
              <DialogTitle>Connect {integration.name}</DialogTitle>
              <DialogDescription>Follow the steps below to set up the integration.</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <StepIndicator current={step} />

        {step === 1 && (
          <div className="space-y-4">
            <h3 className="font-medium text-sm">{integration.steps.setup.title}</h3>
            {integration.steps.setup.instructions}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            {fields.map((field) => (
              <div key={field.key} className="space-y-1.5">
                <Label>{field.label}</Label>
                <Input
                  type={field.type || 'text'}
                  placeholder={field.placeholder}
                  value={fieldValues[field.key] || ''}
                  onChange={(e) => setFieldValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                />
                {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
              </div>
            ))}
          </div>
        )}

        {step === 3 && (
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            {testing && (
              <>
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">Testing connection...</p>
              </>
            )}
            {testResult === 'success' && (
              <>
                <CheckCircle className="h-10 w-10 text-green-500" />
                <p className="text-sm font-medium">Connection successful!</p>
              </>
            )}
            {testResult === 'error' && (
              <>
                <XCircle className="h-10 w-10 text-red-500" />
                <p className="text-sm font-medium text-destructive">Connection failed</p>
                <p className="text-xs text-muted-foreground text-center max-w-xs">{testError}</p>
              </>
            )}
            {testResult === 'idle' && !testing && (
              <p className="text-sm text-muted-foreground">Click "Test" to verify the connection.</p>
            )}
          </div>
        )}

        <DialogFooter className="flex justify-between sm:justify-between">
          <div>
            {step > 1 && step < 3 && (
              <Button variant="ghost" size="sm" onClick={() => setStep(step - 1)}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
            )}
            {step === 3 && testResult === 'error' && (
              <Button variant="ghost" size="sm" onClick={() => setStep(2)}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {step === 1 && (
              <Button onClick={() => setStep(2)}>
                Next <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
            {step === 2 && (
              <Button onClick={() => { setStep(3); setTimeout(runTest, 300); }} disabled={!allFieldsFilled}>
                Next <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
            {step === 3 && testResult === 'error' && (
              <Button onClick={runTest} disabled={testing}>
                {testing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Retry
              </Button>
            )}
            {step === 3 && testResult === 'success' && (
              <Button onClick={handleDone}>Done</Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Generic Configure Dialog (for Jira, Salesforce)
// ---------------------------------------------------------------------------

function ConfigureDialog({
  integration,
  workspace,
  open,
  onOpenChange,
  onDisconnect,
}: {
  integration: IntegrationDefinition;
  workspace: { id: string } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDisconnect: () => void | Promise<void>;
}) {
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<'idle' | 'success' | 'error'>('idle');
  const [testError, setTestError] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const fields = integration.configFields || [];

  // Load saved credentials when opening Configure for Jira
  useEffect(() => {
    if (!open || integration.id !== 'jira' || !workspace) return;
    setLoading(true);
    supabase.rpc('get_jira_connection_status', { p_workspace_id: workspace.id })
      .then(({ data }) => {
        const row = Array.isArray(data) ? data[0] : data;
        if (row?.instance_url || row?.email) {
          setFieldValues((prev) => ({
            ...prev,
            instance_url: row.instance_url || '',
            email: row.email || '',
            api_token: '', // Never expose stored token
          }));
        }
      })
      .finally(() => setLoading(false));
  }, [open, integration.id, workspace?.id]);

  const handleTest = async () => {
    if (integration.id === 'jira') {
      const url = fieldValues['instance_url']?.replace(/\/$/, '');
      const email = fieldValues['email'];
      const token = fieldValues['api_token'];
      if (!url || !email || !token) {
        setTestError('Fill in all fields to test');
        setTestResult('error');
        return;
      }
    }
    setTesting(true);
    setTestResult('idle');
    setTestError('');
    try {
      if (integration.id === 'jira') {
        const url = fieldValues['instance_url']?.replace(/\/$/, '');
        const email = fieldValues['email'];
        const token = fieldValues['api_token'];
        const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
        const fnUrl = `https://${projectId}.supabase.co/functions/v1/jira-test`;
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) throw new Error('Please sign in');
        const res = await fetch(fnUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ instance_url: url, email, api_token: token, workspace_id: workspace?.id }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || data?.message || 'Connection failed');
        if (data?.ok === false || data?.error) throw new Error(data?.error || data?.details || 'Connection failed');
        setTestResult('success');
      } else {
        setTestResult('success');
      }
    } catch (e: any) {
      setTestResult('error');
      setTestError(e?.message || 'Connection failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (integration.id === 'jira' && workspace) {
        const url = fieldValues['instance_url']?.replace(/\/$/, '');
        const email = fieldValues['email'];
        const token = fieldValues['api_token'];
        if (!url || !email) {
          toast.error('Fill in instance URL and email to save');
          return;
        }
        if (token) {
          const { error } = await supabase.rpc('save_jira_connection', {
            p_workspace_id: workspace.id,
            p_instance_url: url,
            p_email: email,
            p_api_token: token,
          });
          if (error) throw error;
        } else {
          const { error } = await supabase.rpc('update_jira_connection_settings', {
            p_workspace_id: workspace.id,
            p_instance_url: url,
            p_email: email,
          });
          if (error) throw error;
        }
      }
      toast.success('Settings saved!', { description: `${integration.name} configuration updated.` });
    } catch (e: any) {
      toast.error('Failed to save', { description: e?.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = () => {
    onDisconnect();
    setConfirmDisconnect(false);
    onOpenChange(false);
    toast.success(`${integration.name} disconnected.`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 bg-muted rounded-lg">{integration.icon}</div>
            <div className="flex-1">
              <DialogTitle>Configure {integration.name}</DialogTitle>
              <DialogDescription>Manage settings for this integration.</DialogDescription>
            </div>
            <Badge variant="default" className="ml-auto">Connected</Badge>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading saved settings...
            </div>
          ) : (
            fields.map((field) => {
              const isTokenField = field.key === 'api_token' && integration.id === 'jira';
              return (
                <div key={field.key} className="space-y-1.5">
                  <Label>{field.label}</Label>
                  <Input
                    type={field.type || 'text'}
                    placeholder={isTokenField ? 'Stored' : field.placeholder}
                    value={fieldValues[field.key] || ''}
                    onChange={(e) => setFieldValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    disabled={isTokenField}
                    className={isTokenField ? 'bg-muted' : undefined}
                  />
                </div>
              );
            })
          )}
        </div>

        {testResult === 'success' && (
          <p className="text-sm text-green-600 flex items-center gap-1"><CheckCircle className="h-4 w-4" /> Connection verified.</p>
        )}
        {testResult === 'error' && (
          <p className="text-sm text-destructive flex items-center gap-1"><XCircle className="h-4 w-4" /> {testError || 'Connection check failed.'}</p>
        )}

        <Separator />

        {!confirmDisconnect ? (
          <DialogFooter className="flex justify-between sm:justify-between">
            <Button variant="destructive" size="sm" onClick={() => setConfirmDisconnect(true)}>
              <Unplug className="h-4 w-4 mr-1" /> Disconnect
            </Button>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleTest}
                disabled={testing || (integration.id === 'jira' && !fieldValues['api_token'])}
                title={integration.id === 'jira' && !fieldValues['api_token'] ? 'Disconnect and reconnect to change API token' : undefined}
              >
                {testing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Test Connection
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </DialogFooter>
        ) : (
          <div className="flex items-center justify-between bg-destructive/10 p-3 rounded-lg">
            <p className="text-sm text-destructive">Are you sure? This will remove the integration.</p>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setConfirmDisconnect(false)}>Cancel</Button>
              <Button variant="destructive" size="sm" onClick={handleDisconnect}>Confirm</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

function getStatusIcon(status: ConnectionStatus) {
  if (status === 'connected') return <CheckCircle className="h-4 w-4 text-green-500" />;
  return <div className="h-4 w-4 rounded-full border-2 border-border" />;
}

// ---------------------------------------------------------------------------
// Integration Request Form
// ---------------------------------------------------------------------------

function IntegrationRequestForm() {
  const { workspace, userProfile } = useWorkspace();
  const [integrationName, setIntegrationName] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workspace) return;
    setSubmitting(true);
    try {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const fnUrl = `https://${projectId}.supabase.co/functions/v1/integration-request`;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error('Please sign in to submit a request');
        return;
      }
      const res = await fetch(fnUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          integration_name: integrationName.trim() || undefined,
          message: message.trim() || undefined,
          user_name: userProfile?.full_name,
          user_email: userProfile?.email,
        }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Failed to send request');
      toast.success('Request sent', { description: "We'll be in touch about your integration request." });
      setIntegrationName('');
      setMessage('');
    } catch (e: any) {
      toast.error('Failed to send request', { description: e?.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-muted rounded-lg">
            <Plug className="h-5 w-5 text-muted-foreground" />
          </div>
          <div>
            <CardTitle className="text-base">Request an integration</CardTitle>
            <CardDescription>Don't see the integration you need? Tell us what you'd like to connect.</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="integration-request-name">Integration or tool name</Label>
            <Input
              id="integration-request-name"
              placeholder="e.g. Linear, Asana, HubSpot"
              value={integrationName}
              onChange={(e) => setIntegrationName(e.target.value)}
              disabled={submitting}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="integration-request-message">Message</Label>
            <Textarea
              id="integration-request-message"
              placeholder="Describe what you'd like to integrate and how you'd use it..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              disabled={submitting}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Your details ({userProfile?.full_name || 'name'}, {userProfile?.email || 'email'}) will be included with the request.
          </p>
          <Button type="submit" disabled={submitting}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
            Send request
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function Integrations() {
  const { workspace, membership } = useWorkspace();
  const userRole = membership?.role as UserRole;

  const [statuses, setStatuses] = useState<Record<string, ConnectionStatus>>({
    slack: 'available',
    jira: 'available',
    salesforce: 'available',
  });

  const [connectingId, setConnectingId] = useState<string | null>(null);
  const [configuringId, setConfiguringId] = useState<string | null>(null);
  const [oauthInProgress, setOauthInProgress] = useState(false);
  const [integrationStats, setIntegrationStats] = useState<{ errors_last_week: number; events_processed: number } | null>(null);

  const fetchIntegrationStats = useCallback(async () => {
    if (!workspace?.id) return;
    try {
      const { data } = await supabase.rpc('get_integration_stats', { p_workspace_id: workspace.id });
      if (data) setIntegrationStats(data as any);
    } catch { /* silent */ }
  }, [workspace?.id]);

  useEffect(() => {
    fetchIntegrationStats();
  }, [fetchIntegrationStats]);

  // Fetch real Slack status from DB
  const fetchSlackStatus = useCallback(async (forWorkspaceId?: string) => {
    const id = forWorkspaceId ?? workspace?.id;
    if (!id) return;
    const { data } = await supabase.rpc('get_slack_connection_status', { p_workspace_id: id });
    const row = Array.isArray(data) ? data[0] : data;
    if (id === workspace?.id) {
      setStatuses((prev) => ({
        ...prev,
        slack: row?.slack_team_id ? 'connected' : 'available',
      }));
    }
  }, [workspace?.id]);

  // Fetch real Jira status from DB
  const fetchJiraStatus = useCallback(async (forWorkspaceId?: string) => {
    const id = forWorkspaceId ?? workspace?.id;
    if (!id) return;
    const { data } = await supabase.rpc('get_jira_connection_status', { p_workspace_id: id });
    const row = Array.isArray(data) ? data[0] : data;
    if (id === workspace?.id) {
      setStatuses((prev) => ({
        ...prev,
        jira: row?.connected ? 'connected' : 'available',
      }));
    }
  }, [workspace?.id]);

  useEffect(() => {
    fetchSlackStatus();
  }, [fetchSlackStatus]);

  useEffect(() => {
    fetchJiraStatus();
  }, [fetchJiraStatus]);

  // Handle Slack OAuth callback (?code=...&state=... on /integrations)
  const oauthProcessedRef = useRef(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    if (!code || !state || oauthProcessedRef.current) return;

    const workspaceId = getWorkspaceIdFromSlackState(state);
    if (!workspaceId) {
      toast.error('Slack connection expired or invalid. Please try again.');
      window.history.replaceState({}, '', window.location.pathname || '/');
      return;
    }

    oauthProcessedRef.current = true;

    const exchangeAndSave = async () => {
      setOauthInProgress(true);
      try {
        const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
        const fnUrl = `https://${projectId}.supabase.co/functions/v1/slack-oauth-callback`;
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          toast.error('Please sign in to connect Slack');
          oauthProcessedRef.current = false;
          return;
        }
        const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;
        const redirectUri = `${appUrl.replace(/\/$/, '')}/integrations`;
        const res = await fetch(fnUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ code, state, workspace_id: workspaceId, redirect_uri: redirectUri }),
        });
        const result = await res.json();
        if (!res.ok) throw new Error(result.error || 'Failed to connect Slack');

        await fetchSlackStatus(workspaceId);
        toast.success('Slack connected!', { description: 'Click Configure to select a notification channel.' });
        window.history.replaceState({}, '', window.location.pathname || '/');
        setConfiguringId('slack');
      } catch (e: any) {
        toast.error('Failed to connect Slack', { description: e?.message });
        oauthProcessedRef.current = false;
      } finally {
        setOauthInProgress(false);
      }
    };

    exchangeAndSave();
  }, [fetchSlackStatus]);

  const connectedCount = Object.values(statuses).filter((s) => s === 'connected').length;
  const connectingIntegration = INTEGRATIONS.find((i) => i.id === connectingId) || null;
  const configuringIntegration = INTEGRATIONS.find((i) => i.id === configuringId) || null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1>Integrations</h1>
        <p className="text-muted-foreground">Connect external tools to collect feedback and sync data.</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-950/40">
                <Plug className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{connectedCount}</p>
                <p className="text-xs text-muted-foreground">Active integrations</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${(integrationStats?.errors_last_week ?? 0) > 0 ? 'bg-red-100 dark:bg-red-950/40' : 'bg-muted'}`}>
                <AlertTriangle className={`h-5 w-5 ${(integrationStats?.errors_last_week ?? 0) > 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`} />
              </div>
              <div>
                <p className="text-2xl font-bold">{integrationStats?.errors_last_week ?? '–'}</p>
                <p className="text-xs text-muted-foreground">Errors (last 7 days)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-950/40">
                <Activity className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{integrationStats?.events_processed?.toLocaleString() ?? '–'}</p>
                <p className="text-xs text-muted-foreground">Events processed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* OAuth in-progress overlay */}
      {oauthInProgress && (
        <Card className="border-primary/50">
          <CardContent className="flex items-center justify-center gap-3 py-6">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Connecting Slack...</p>
          </CardContent>
        </Card>
      )}

      {/* Integrations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {INTEGRATIONS.map((integration) => {
          const status = statuses[integration.id] || 'available';
          const isConnected = status === 'connected';

          return (
            <Card
              key={integration.id}
              className={`transition-all duration-200 hover:shadow-md ${isConnected ? 'border-green-200' : ''}`}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-muted rounded-lg">{integration.icon}</div>
                    <div>
                      <CardTitle className="text-base">{integration.name}</CardTitle>
                      <Badge variant="outline" className="text-xs mt-1">{integration.category}</Badge>
                    </div>
                  </div>
                  {getStatusIcon(status)}
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="mb-4">{integration.description}</CardDescription>

                <div className="flex space-x-2">
                  {isConnected ? (
                    hasPermission(userRole, 'canConfigureIntegrations') && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => setConfiguringId(integration.id)}
                      >
                        <Settings className="h-4 w-4 mr-2" />
                        Configure
                      </Button>
                    )
                  ) : (
                    hasPermission(userRole, 'canConfigureIntegrations') && (
                      <Button size="sm" className="flex-1" onClick={() => setConnectingId(integration.id)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Connect
                      </Button>
                    )
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Integration Request Form */}
      <IntegrationRequestForm />

      {/* Slack Connect/Configure Wizard */}
      {(connectingId === 'slack' || configuringId === 'slack') && (
        <SlackConnectWizard
          open
          onOpenChange={(v) => {
            if (!v) {
              setConnectingId(null);
              setConfiguringId(null);
            }
          }}
          isConnected={statuses.slack === 'connected'}
          onDisconnect={() => {
            setStatuses((prev) => ({ ...prev, slack: 'available' }));
            setConfiguringId(null);
            setConnectingId(null);
          }}
          onConnected={() => {
            setStatuses((prev) => ({ ...prev, slack: 'connected' }));
            setConnectingId(null);
            setConfiguringId(null);
          }}
        />
      )}

      {/* Credentials Connect Wizard (Jira, Salesforce) */}
      {connectingIntegration && connectingId !== 'slack' && (
        <ConnectWizard
          integration={connectingIntegration}
          workspace={workspace}
          open={!!connectingId}
          onOpenChange={(v) => { if (!v) setConnectingId(null); }}
          onConnected={() => {
            setStatuses((prev) => ({ ...prev, [connectingIntegration.id]: 'connected' }));
            setConnectingId(null);
          }}
        />
      )}

      {/* Generic Configure Dialog (Jira, Salesforce) */}
      {configuringIntegration && configuringId !== 'slack' && (
        <ConfigureDialog
          integration={configuringIntegration}
          workspace={workspace}
          open={!!configuringId}
          onOpenChange={(v) => { if (!v) setConfiguringId(null); }}
          onDisconnect={async () => {
            if (configuringIntegration.id === 'jira' && workspace) {
              const { error } = await supabase.rpc('delete_jira_connection', { p_workspace_id: workspace.id });
              if (error) {
                toast.error('Failed to disconnect', { description: error.message });
                return;
              }
            }
            setStatuses((prev) => ({ ...prev, [configuringIntegration.id]: 'available' }));
            setConfiguringId(null);
          }}
        />
      )}
    </div>
  );
}
